import React, { useEffect, useRef, useState } from 'react';
import { useCursor } from '../hooks/useCursor';

interface CursorProps {
  className?: string;
}

export const Cursor: React.FC<CursorProps> = ({ className = '' }) => {
  const { position, trails, isMoving, velocity } = useCursor();
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);

  // Check if device is mobile or tablet
  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 1024; // tablets and below
      setIsMobile(isTouchDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleMouseEnter = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('button, a, [role="button"], input, textarea, select, .interactive')) {
        setHoveredElement(target);
        target.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      }
    };

    const handleMouseLeave = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('button, a, [role="button"], input, textarea, select, .interactive')) {
        setHoveredElement(null);
        target.style.transform = 'translate(0, 0)';
      }
    };

    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);

    return () => {
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
    };
  }, []);

  useEffect(() => {
    if (hoveredElement && cursorRef.current) {
      const cursorRect = cursorRef.current.getBoundingClientRect();
      const elementRect = hoveredElement.getBoundingClientRect();
      
      const cursorCenterX = cursorRect.left + cursorRect.width / 2;
      const cursorCenterY = cursorRect.top + cursorRect.height / 2;
      const elementCenterX = elementRect.left + elementRect.width / 2;
      const elementCenterY = elementRect.top + elementRect.height / 2;
      
      const deltaX = cursorCenterX - elementCenterX;
      const deltaY = cursorCenterY - elementCenterY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance < 100) {
        const pullStrength = Math.max(0, (100 - distance) / 100) * 8;
        const pullX = (deltaX / distance) * pullStrength;
        const pullY = (deltaY / distance) * pullStrength;
        
        hoveredElement.style.transform = `translate(${pullX}px, ${pullY}px)`;
      }
    }
  }, [position, hoveredElement]);

  const orbSize = hoveredElement ? 24 : 16;
  const orbOpacity = isMoving ? Math.min(0.8 + velocity * 0.2, 1) : 0.6;

  if (isMobile) return null;

  return (
    <>
      {/* Hide default cursor */}
      <style>{`
        * {
          cursor: none !important;
        }
      `}</style>
      
      {/* Trail effects */}
      {trails.map((trail) => (
        <div
          key={trail.id}
          className="fixed pointer-events-none"
           style={{
             left: trail.x - 6,
             top: trail.y - 6,
             width: 12,
             height: 12,
             opacity: trail.opacity,
             background: 'radial-gradient(circle, rgba(59, 130, 246, 0.8) 0%, rgba(147, 51, 234, 0.6) 50%, transparent 70%)',
             borderRadius: '50%',
             mixBlendMode: 'screen',
             filter: 'blur(1px)',
             transform: 'scale(0.8)',
             zIndex: 2147483646, // High z-index for trail elements
           }}

        />
      ))}
      
      {/* Main cursor orb */}
      <div
        ref={cursorRef}
        className={`fixed pointer-events-none ${className}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) scale(${hoveredElement ? 1.5 : 1})`,
          transition: hoveredElement ? 'transform 0.2s ease-out' : 'none',
          zIndex: 2147483647, // Maximum z-index value
          width: orbSize,
          height: orbSize,
          opacity: orbOpacity,
        }}

      >
        {/* Outer glow */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(147, 51, 234, 0.3) 40%, rgba(236, 72, 153, 0.2) 70%, transparent 100%)',
            mixBlendMode: 'screen',
            filter: 'blur(4px)',
            transform: 'scale(2)',
          }}
        />
        
        {/* Inner core */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(59, 130, 246, 0.8) 30%, rgba(147, 51, 234, 0.6) 60%, transparent 100%)',
            mixBlendMode: 'screen',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(147, 51, 234, 0.3)',
          }}
        />
        
        {/* Pulsing effect when hovering */}
        {hoveredElement && (
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.6) 0%, transparent 70%)',
              mixBlendMode: 'screen',
            }}
          />
        )}
      </div>
    </>
  );
};

export default Cursor;